import { progressPerHour } from './model.js';
/** Integration step, hours. */
const DT_H = 0.05;
/** An open segment that has not reached the end point after this long never will, for us. */
export const MAX_OPEN_H = 21 * 24;
/** The same search horizon in seconds, as reported by `beyondHorizon` bounds. */
export const HORIZON_S = MAX_OPEN_H * 3600;
const temperatureAt = (p, fromC, ambientC, h) => p.tauH === 0 ? ambientC : ambientC + (fromC - ambientC) * Math.exp(-h / p.tauH);
/**
 * Once the dough is within this of the ambient (°C), the rest of the segment is integrated in
 * closed form: the rate is linearized around the ambient, `f ≈ f_a × (1 + κ δ e^(−h/τ))`, whose
 * integral is exact; the neglected term is of order (κ δ)², about 1e-6 of the rate here. Long cold
 * retards would otherwise cost thousands of integration steps. Same model, same equations.
 */
const SETTLED_C = 0.01;
/** The closed-form rest of a segment from `h`, when the dough is settled there. */
const tailFrom = (p, freshPct, fromC, ambientC, h) => {
    const delta = temperatureAt(p, fromC, ambientC, h) - ambientC;
    if (Math.abs(delta) >= SETTLED_C)
        return undefined;
    const rate = progressPerHour(p, freshPct, ambientC);
    // d ln f / dT at the ambient, numerically, so that this does not restate the rate law.
    const eps = 0.01;
    const kappa = rate > 0
        ? (Math.log(progressPerHour(p, freshPct, ambientC + eps)) -
            Math.log(progressPerHour(p, freshPct, ambientC - eps))) /
            (2 * eps)
        : 0;
    const tau = p.tauH;
    const over = (hours) => rate * (hours + (tau === 0 ? 0 : kappa * delta * tau * (1 - Math.exp(-hours / tau))));
    const hoursFor = (needed, within) => {
        if (needed <= 0)
            return 0;
        if (!(rate > 0) || over(within) < needed)
            return undefined;
        let hours = Math.min(within, needed / rate);
        for (let i = 0; i < 20; i++) {
            const slope = rate * (1 + (tau === 0 ? 0 : kappa * delta * Math.exp(-hours / tau)));
            const next = Math.min(within, Math.max(0, hours - (over(hours) - needed) / slope));
            if (Math.abs(next - hours) < 1e-12)
                return next;
            hours = next;
        }
        return hours;
    };
    return { rate, over, hoursFor };
};
/** Progress gained over [h0, h1] of a segment (Simpson on one interval). */
const gain = (p, freshPct, fromC, ambientC, h0, h1) => {
    const f = (h) => progressPerHour(p, freshPct, temperatureAt(p, fromC, ambientC, h));
    return ((h1 - h0) / 6) * (f(h0) + 4 * f((h0 + h1) / 2) + f(h1));
};
/**
 * Forward model of docs/contracts/fermentation.md: one progress state, never reset between
 * segments; the dough temperature follows each ambient with a first-order lag.
 */
export const simulate = (p, input) => {
    const target = input.target ?? 1;
    let progress = 0;
    let doughC = input.startC;
    for (const [index, segment] of input.segments.entries()) {
        const open = segment.hours === undefined;
        if (open && index !== input.segments.length - 1)
            throw new Error('only the last is open');
        if (open && progress >= target)
            return { kind: 'remaining', hours: 0, reachedBefore: true };
        const total = segment.hours ?? MAX_OPEN_H;
        const n = Math.max(1, Math.ceil(total / DT_H));
        const dt = total / n;
        for (let i = 0; i < n; i++) {
            const tail = tailFrom(p, input.freshPct, doughC, segment.ambientC, i * dt);
            if (tail) {
                const left = total - i * dt;
                if (open) {
                    const needed = tail.hoursFor(target - progress, left);
                    return needed === undefined
                        ? { kind: 'notReached' }
                        : { kind: 'remaining', hours: i * dt + needed, reachedBefore: false };
                }
                progress += tail.over(left);
                break;
            }
            const g = gain(p, input.freshPct, doughC, segment.ambientC, i * dt, (i + 1) * dt);
            if (open && progress + g >= target) {
                return {
                    kind: 'remaining',
                    hours: i * dt + (dt * (target - progress)) / g,
                    reachedBefore: false,
                };
            }
            progress += g;
        }
        if (open)
            return { kind: 'notReached' };
        doughC = temperatureAt(p, doughC, segment.ambientC, total);
    }
    return { kind: 'progress', progress };
};
/** Runs one planned segment, with the same steps as `simulate`. */
const advance = (p, freshPct, state, segment) => {
    const n = Math.max(1, Math.ceil(segment.hours / DT_H));
    const dt = segment.hours / n;
    let progress = state.progress;
    for (let i = 0; i < n; i++) {
        const tail = tailFrom(p, freshPct, state.doughC, segment.ambientC, i * dt);
        if (tail) {
            progress += tail.over(segment.hours - i * dt);
            break;
        }
        progress += gain(p, freshPct, state.doughC, segment.ambientC, i * dt, (i + 1) * dt);
    }
    return { progress, doughC: temperatureAt(p, state.doughC, segment.ambientC, segment.hours) };
};
/**
 * The hours the one open segment needs so that progress reaches the target at the end of the
 * schedule, every other segment as planned (fermentation.md, Model-driven duration). An open last
 * segment is `simulate`; an open earlier one is solved by bisection on the same forward model,
 * never by a separate formula. Segments other than the open one need their hours.
 */
export const solveOpenSegment = (p, input) => {
    const index = input.segments.findIndex((s) => s.hours === undefined);
    if (index < 0 || index === input.segments.length - 1)
        return simulate(p, input);
    const target = input.target ?? 1;
    const planned = (s) => ({ hours: s.hours ?? 0, ambientC: s.ambientC });
    let start = { progress: 0, doughC: input.startC };
    for (const s of input.segments.slice(0, index))
        start = advance(p, input.freshPct, start, planned(s));
    const ambientC = input.segments[index].ambientC;
    const tail = input.segments.slice(index + 1).map(planned);
    const at = (hours) => tail.reduce((state, s) => advance(p, input.freshPct, state, s), advance(p, input.freshPct, start, { hours, ambientC })).progress;
    if (at(0) >= target)
        return { kind: 'remaining', hours: 0, reachedBefore: true };
    // Doubling, then bisection: the open step is usually hours, not weeks.
    let low = 0;
    let high = 1;
    while (at(high) < target) {
        if (high >= MAX_OPEN_H)
            return { kind: 'notReached' };
        low = high;
        high = Math.min(MAX_OPEN_H, high * 2);
    }
    for (let i = 0; i < 30 && high - low > 1e-4; i++) {
        const mid = (low + high) / 2;
        if (at(mid) >= target)
            high = mid;
        else
            low = mid;
    }
    return { kind: 'remaining', hours: (low + high) / 2, reachedBefore: false };
};
/**
 * Inverse model: the factor applied to the dose so that progress reaches the target at the end of a fully
 * planned schedule, by bisection on `simulate` (never a separate formula).
 */
export const solveDoseFactor = (p, input) => {
    const progressWith = (factor) => {
        const result = simulate(p, { ...input, freshPct: input.freshPct * factor });
        return result.kind === 'progress' ? result.progress : NaN;
    };
    let low = Math.log(1e-4);
    let high = Math.log(1e4);
    const target = input.target ?? 1;
    if (!(progressWith(Math.exp(high)) >= target) || progressWith(Math.exp(low)) >= target) {
        return undefined;
    }
    for (let i = 0; i < 40; i++) {
        const mid = (low + high) / 2;
        if (progressWith(Math.exp(mid)) >= target)
            high = mid;
        else
            low = mid;
    }
    return Math.exp((low + high) / 2);
};
//# sourceMappingURL=simulate.js.map